import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Anchor, Globe, Check } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { LANGUAGES } from "../i18n/translations";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const Navbar = () => {
  const { lang, setLang, t } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const links = [
    { to: "/", label: t.nav.home, end: true },
    { to: "/about", label: t.nav.about },
    { to: "/activities", label: t.nav.activities },
    { to: "/booking", label: t.nav.booking },
    { to: "/contact", label: t.nav.contact },
  ];

  const activeLang = LANGUAGES.find((l) => l.code === lang) || LANGUAGES[0];

  return (
    <header
      data-testid="site-navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-white/80 backdrop-blur-xl border-b border-black/5" : "bg-transparent"
      }`}
    >
      <div className="container-x flex items-center justify-between py-4 md:py-5">
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-2 group">
          <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#0B192C] text-white transition-transform group-hover:-rotate-12">
            <Anchor size={16} strokeWidth={1.8} />
          </span>
          <span className="h-display text-xl md:text-[22px] font-medium tracking-tight text-[#0B192C]">
            Lofoten <span className="font-light italic text-[#183059]">By Sea</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={`nav-${l.to === "/" ? "home" : l.to.slice(1)}`}
              className={({ isActive }) =>
                `text-[13px] tracking-wide transition-colors ${
                  scrolled
                    ? isActive
                      ? "text-[#0B192C] font-semibold"
                      : "text-[#475569] hover:text-[#0B192C]"
                    : isActive
                      ? "text-white font-semibold"
                      : "text-white/90 hover:text-white"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div className="hidden md:block">
            <DropdownMenu>
              <DropdownMenuTrigger
                className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-white/60 backdrop-blur px-3.5 py-2 text-xs font-medium tracking-[0.1em] uppercase text-[#0B192C] hover:bg-white transition-colors focus:outline-none focus:ring-2 focus:ring-[#0B192C]/20"
                data-testid="language-toggle"
                aria-label="Select language"
              >
                <Globe size={14} strokeWidth={1.8} />
                {activeLang.short}
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="min-w-[180px] rounded-xl border border-black/5 bg-white/95 backdrop-blur-xl shadow-[0_20px_40px_-20px_rgba(11,25,44,0.25)] p-1.5"
                data-testid="language-menu"
              >
                {LANGUAGES.map((lng) => (
                  <DropdownMenuItem
                    key={lng.code}
                    onClick={() => setLang(lng.code)}
                    data-testid={`lang-${lng.code}`}
                    className={`flex items-center justify-between cursor-pointer rounded-lg px-3 py-2 text-[13px] ${
                      lng.code === lang
                        ? "bg-[#F7F5F0] font-semibold text-[#0B192C]"
                        : "text-[#475569] hover:bg-[#F7F5F0] hover:text-[#0B192C]"
                    }`}
                  >
                    <span className="flex items-center gap-3">
                      <span className="w-6 text-[10px] tracking-[0.15em] text-[#94A3B8]">{lng.short}</span>
                      <span>{lng.label}</span>
                    </span>
                    {lng.code === lang && <Check size={14} />}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <Link to="/booking" className="hidden md:inline-flex btn-primary" data-testid="nav-book-now">
            {t.nav.book_now}
          </Link>

          <button
            onClick={() => setOpen((o) => !o)}
            className="lg:hidden inline-flex h-10 w-10 items-center justify-center rounded-full border border-black/10 text-[#0B192C]"
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-black/5 bg-white" data-testid="mobile-menu">
          <div className="container-x py-6 flex flex-col gap-4">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                className={({ isActive }) =>
                  `text-base ${isActive ? "text-[#0B192C] font-semibold" : "text-[#475569]"}`
                }
                data-testid={`mobile-nav-${l.to === "/" ? "home" : l.to.slice(1)}`}
              >
                {l.label}
              </NavLink>
            ))}

            <div className="pt-4 border-t border-black/5">
              <p className="text-[11px] uppercase tracking-[0.2em] text-[#94A3B8] mb-3 flex items-center gap-2">
                <Globe size={12} /> {t.extras.nav_language}
              </p>
              <div className="grid grid-cols-2 gap-2">
                {LANGUAGES.map((lng) => (
                  <button
                    key={lng.code}
                    onClick={() => setLang(lng.code)}
                    data-testid={`mobile-lang-${lng.code}`}
                    className={`flex items-center justify-between rounded-lg px-3 py-2.5 text-sm transition-colors ${
                      lng.code === lang
                        ? "bg-[#0B192C] text-white"
                        : "bg-[#F7F5F0] text-[#0B192C] hover:bg-[#E5D9C5]"
                    }`}
                  >
                    <span>{lng.label}</span>
                    <span className="text-[10px] tracking-[0.15em] opacity-70">{lng.short}</span>
                  </button>
                ))}
              </div>
            </div>

            <Link to="/booking" className="btn-primary w-fit mt-2" data-testid="mobile-book-now">
              {t.nav.book_now}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
